import type { CategoryResponse } from '@ydistri/api-sdk';
import type { MultiselectCustomerPartialEntity } from '../../global/ConfigurationMultiselect/configurationMultiselectTypes';

const CATEGORY_LEVEL_INDENT = '\u00A0\u00A0\u00A0\u00A0';

const compareCategories = (a: CategoryResponse, b: CategoryResponse): number =>
  a.title.localeCompare(b.title) || a.id - b.id;

const getAncestorTitles = (
  category: CategoryResponse,
  categoriesById: Map<number, CategoryResponse>,
): string[] => {
  const ancestorTitles: string[] = [];
  const visitedIds = new Set([category.id]);
  let parentCategoryId = category.parentCategoryId;

  while (parentCategoryId !== null && parentCategoryId !== undefined) {
    if (visitedIds.has(parentCategoryId)) break;
    visitedIds.add(parentCategoryId);

    const parent = categoriesById.get(parentCategoryId);
    if (!parent) break;

    if (parent.level > 0) ancestorTitles.unshift(parent.title);
    parentCategoryId = parent.parentCategoryId;
  }

  return ancestorTitles;
};

export const toScopeCategoryOptions = (
  categories: CategoryResponse[],
): MultiselectCustomerPartialEntity[] => {
  const categoriesById = new Map(categories.map(category => [category.id, category]));
  const selectableCategories = [...categoriesById.values()].filter(category => category.level > 0);
  const selectableCategoryIds = new Set(selectableCategories.map(category => category.id));
  const childrenByParentId = new Map<number, CategoryResponse[]>();

  selectableCategories.forEach(category => {
    if (category.parentCategoryId === null || category.parentCategoryId === undefined) return;

    const children = childrenByParentId.get(category.parentCategoryId) ?? [];
    children.push(category);
    childrenByParentId.set(category.parentCategoryId, children);
  });

  childrenByParentId.forEach(children => children.sort(compareCategories));

  const orderedCategories: CategoryResponse[] = [];
  const visitedIds = new Set<number>();
  const visitCategory = (category: CategoryResponse): void => {
    if (visitedIds.has(category.id)) return;

    visitedIds.add(category.id);
    orderedCategories.push(category);
    childrenByParentId.get(category.id)?.forEach(visitCategory);
  };

  selectableCategories
    .filter(category => category.level === 1)
    .sort(compareCategories)
    .forEach(visitCategory);

  selectableCategories
    .filter(
      category =>
        !visitedIds.has(category.id) &&
        (category.parentCategoryId === null ||
          category.parentCategoryId === undefined ||
          !selectableCategoryIds.has(category.parentCategoryId)),
    )
    .sort(compareCategories)
    .forEach(visitCategory);

  selectableCategories
    .filter(category => !visitedIds.has(category.id))
    .sort((a, b) => a.level - b.level || compareCategories(a, b))
    .forEach(visitCategory);

  return orderedCategories.map(category => {
    const ancestorTitles = getAncestorTitles(category, categoriesById);
    const indent = CATEGORY_LEVEL_INDENT.repeat(Math.max(0, category.level - 1));
    const categoryPath = ancestorTitles.length > 0 ? ` [${ancestorTitles.join(' -> ')}]` : '';

    return {
      id: category.id,
      customerId: category.id.toString(),
      title: category.title,
      code: category.code,
      label: `${indent}${category.title}${categoryPath}`,
    };
  });
};
